import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { StudentClass, Pupil } from '@app/definitions/interfaces';

@Component({
    selector: 'app-individual-data-plots',
    templateUrl: './individual-data-plots.component.html',
    styleUrls: ['./individual-data-plots.component.scss']
})
export class IndividualDataPlotsComponent implements OnInit, OnChanges {
    @Input() lessonsStats;
    @Input() boxesStats;
    @Input() studentClass: StudentClass;
    @Input() currentPupil: Pupil;

    lessonChartData: any;
    boxesChartData: any;
    lessonChartLabels: Array<string> = ['Занятие', 'Решено вариантов', 'Ср. % ошибок', 'Заработано'];
    boxesChartLabels: Array<string> = ['Бокс'];
    colors = {
        variants: '#495660',
        mistakes: '#ca363a',
        earned: '#4e77af',

        max: '#777',
        min: '#777',
        median: '#4e77af',
        interval: '#4e77af',
        currentPupil: '#ca363a'
    };

    constructor() {}

    ngOnInit() {}

    ngOnChanges() {
        this.processLessonStats();
        this.processBoxesStats();
    }

    processLessonStats() {
        const lessonsStats = this.lessonsStats.slice(0);
        const minMaxes = lessonsStats.reduce(
            (memo, value) => {
                if (value[1] < memo.min.variants) {
                    memo.min.variants = value[1];
                }
                if (value[1] > memo.max.variants) {
                    memo.max.variants = value[1];
                }
                if (value[2] < memo.min.mistakes) {
                    memo.min.mistakes = value[2];
                }
                if (value[2] > memo.max.mistakes) {
                    memo.max.mistakes = value[2];
                }
                if (value[3] < memo.min.earned) {
                    memo.min.earned = value[3];
                }
                if (value[3] > memo.max.earned) {
                    memo.max.earned = value[3];
                }
                return memo;
            },
            {
                min: { variants: Infinity, mistakes: Infinity, earned: Infinity },
                max: { variants: -Infinity, mistakes: -Infinity, earned: -Infinity }
            }
        );

        this.lessonChartData = {
            chartType: 'LineChart',
            dataTable: lessonsStats[0],
            options: {
                title: 'Прогресс ученика',
                height: 400,
                width: 360,
                legend: { position: 'none' },
                focusTarget: 'category',
                interpolateNulls: true,
                series: {
                    // Gives each series an axis name that matches the Y-axis below.
                    0: { targetAxisIndex: 0, color: this.colors.variants },
                    1: { targetAxisIndex: 1, color: this.colors.mistakes },
                    2: { targetAxisIndex: 2, color: this.colors.earned }
                },
                hAxis: {
                    // gridlines: Они не могут быть нарисованы, потому что названия уроков имеет строковый тип
                    // Но я их оставлю здесь, чтобы знать как их рисовать
                    // @see continuous axis https://developers.google.com/chart/interactive/docs/customizing_axes#Terminology
                    gridlines: {
                        color: '#f1f1f1'
                    },
                    title: 'Занятия',
                    titleTextStyle: { color: this.colors.variants, italic: false }
                },
                vAxis: {
                    gridline: {
                        color: '#f1f1f1',
                        count: 5
                    }
                },
                vAxes: {
                    // Adds labels to each axis; they don't have to match the axis names.
                    0: {
                        title: 'Решено вариантов',
                        titleTextStyle: { color: this.colors.variants, italic: false },
                        textPosition: 'out',
                        textStyle: { color: this.colors.variants },
                        minValue: Math.max(0, minMaxes.min.variants - 10),
                        maxValue: minMaxes.max.variants + 5
                    },
                    1: {
                        title: 'Ср. % ошибок',
                        titleTextStyle: { color: this.colors.mistakes, italic: false },
                        titlePosition: 'in',
                        textPosition: 'in',
                        textStyle: { color: this.colors.mistakes },
                        minValue: Math.max(0, minMaxes.min.mistakes - 10),
                        maxValue: minMaxes.max.mistakes + 5
                    },
                    2: {
                        title: 'Заработано',
                        titleTextStyle: { color: this.colors.earned, italic: false },
                        textPosition: 'out',
                        textStyle: { color: this.colors.earned },
                        minValue: Math.max(0, minMaxes.min.earned - 10),
                        maxValue: minMaxes.max.earned + 5
                    }
                }
            }
        };
    }

    processBoxesStats() {
        // Copy array, because otherwise it will override data,
        // if you pass the same data
        let boxesStats = this.boxesStats.map(arr => arr.slice(0));

        // @ts-ignore-start: игнарируем параметр "s" так как он не используется,
        // а линтер ругается
        const studentsArray = boxesStats[0].slice(1).map((s, idx) => {
            // @ts-ignore-end
            const pupil = this.studentClass.students[idx];
            return `${pupil.surname} ${pupil.name}`;
        });

        const currentPupilIdx = this.studentClass.students.findIndex(p => p.id === this.currentPupil.id);

        boxesStats = this.getBoxPlotValues(boxesStats, currentPupilIdx);

        this.boxesChartLabels = [
            'Бокс',
            ...studentsArray,
            { id: 'max', type: 'number', role: 'interval' },
            { id: 'currentPupil', type: 'number', role: 'interval' },
            { id: 'min', type: 'number', role: 'interval' },
            { id: 'firstQuartile', type: 'number', role: 'interval' },
            { id: 'median', type: 'number', role: 'interval' },
            { id: 'thirdQuartile', type: 'number', role: 'interval' }
        ];

        this.boxesChartData = {
            chartType: 'LineChart',
            dataTable: boxesStats,
            options: {
                title: 'Результаты ученика относительно класса',
                focusTarget: 'category',
                height: 400,
                width: 360,
                legend: { position: 'none' },
                lineWidth: 0,
                intervals: {
                    barWidth: 1,
                    shortBarWidth: 0.75,
                    boxWidth: 1,
                    lineWidth: 2,
                    style: 'boxes'
                },
                tooltip: {
                    trigger: 'none'
                },
                series: [{ color: this.colors.variants }],
                interval: {
                    max: {
                        style: 'bars',
                        fillOpacity: 1,
                        color: this.colors.max
                    },
                    min: {
                        style: 'bars',
                        fillOpacity: 1,
                        color: this.colors.min
                    },
                    currentPupil: {
                        style: 'points',
                        fillOpacity: 1,
                        color: this.colors.currentPupil
                    },
                    firstQuartile: {
                        fillOpacity: 0.3,
                        color: this.colors.interval
                    },
                    thirdQuartile: {
                        fillOpacity: 0.3,
                        color: this.colors.interval
                    },
                    median: {
                        fillOpacity: 1,
                        color: this.colors.median
                    }
                },
                hAxis: {
                    // gridlines: Они не могут быть нарисованы, потому что названия уроков имеет строковый тип
                    // Но я их оставлю здесь, чтобы знать как их рисовать
                    // @see continuous axis https://developers.google.com/chart/interactive/docs/customizing_axes#Terminology
                    gridlines: {
                        color: '#f1f1f1'
                    },
                    title: 'Boxes',
                    titleTextStyle: { color: this.colors.variants, italic: false }
                },
                vAxes: {
                    0: {
                        title: 'Решено вариантов',
                        titleTextStyle: { color: this.colors.variants, italic: false },
                        textPosition: 'out',
                        textStyle: { color: this.colors.variants }
                    }
                }
            }
        };
    }

    /**
     * Takes an array of input data and returns an
     * array of the input data with the box plot
     * interval data appended to each row.
     */
    getBoxPlotValues(array, currentPupilIdx) {
        for (let i = 0; i < array.length; i++) {
            const arr = array[i].slice(1).sort(function(a, b) {
                return a - b;
            });

            const max = arr[arr.length - 1];
            const min = arr[0];
            const median = this.getMedian(arr);

            // First Quartile is the median from lowest to overall median.
            const firstQuartile = this.getMedian(arr.slice(0, Math.round(arr.length / 2)));

            // Third Quartile is the median from the overall median to the highest.
            const thirdQuartile = this.getMedian(arr.slice(Math.round(arr.length / 2) - 1));

            array[i][array[i].length] = max;
            array[i][array[i].length] = array[i][currentPupilIdx + 1];
            array[i][array[i].length] = min;
            array[i][array[i].length] = firstQuartile;
            array[i][array[i].length] = median;
            array[i][array[i].length] = thirdQuartile;
        }
        return array;
    }

    /*
     * Takes an array and returns
     * the median value.
     */
    getMedian(array) {
        const length = array.length;

        /**
         * If the array is an even length the
         * median is the average of the two
         * middle-most values. Otherwise the
         * median is the middle-most value.
         */
        if (length % 2 === 0) {
            const midUpper = length / 2;
            const midLower = midUpper - 1;
            return (array[midUpper] + array[midLower]) / 2;
        } else {
            return array[Math.floor(length / 2)];
        }
    }
}
